import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { FormSelect } from 'components/molecules/FormSelect';
import { useForm } from 'react-hook-form';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { SearchWrapper } from 'components/atoms/SearchWrapper';
import { FormInput } from 'components/molecules/FormInput';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { SelectedFilters } from 'components/organisms/SelectedFilters';
import { ESearchFilterKeys } from 'constants/SearchFilterTypes';
import { prepareFilters } from 'helpers/filters';
import { useTranslations } from 'hooks/useTranslations';
import { FormAutocomplete } from 'components/molecules/FormAutocomplete';
import { useFirearms } from 'pages/Firearms/hooks';
import { useLang } from 'models/langContext';
import { EDictionaryTypes } from 'models/dictionary';
import { useGlobalProperty } from 'models/globalPropertyContext';

export interface ISearchFormValues {
  fireCycleId: string;
  make: string;
  model: string;
  calibre: string;
  action: string;
  type: string;
  serialNumber: string;
}

export enum ESearchFormFirearmLabels {
  serialNumber = 'registeredFirearms.accordion.serialNumber.label',
  fireCycleId = 'registeredFirearms.accordion.fireCycleId.label',
  make = 'registeredFirearms.accordion.make.label',
  model = 'registeredFirearms.accordion.model.label',
  calibre = 'registeredFirearms.accordion.calibre.label',
  action = 'registeredFirearms.accordion.firearmAction.label',
  type = 'registeredFirearms.accordion.type.label',
}

export const initialForm: ISearchFormValues = {
  fireCycleId: '',
  make: '',
  model: '',
  calibre: '',
  action: '',
  type: '',
  serialNumber: '',
};

interface IFirearmsSearchForm {
  onSubmit: (arg: any) => void;
}

const FirearmsSearchForm = ({ onSubmit }: IFirearmsSearchForm) => {
  const [values, setValues] = useState<ISearchFormValues>(initialForm);
  const { t } = useTranslations();
  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<ISearchFormValues>({
    defaultValues: initialForm,
  });
  const { selectedLanguage } = useLang();

  const {
    loadDictionariesForRegisteredFirearms,
    make,
    model,
    caliber,
    firearmAction,
    firearmType,
  } = useFirearms();

  const { getProperties } = useGlobalProperty();

  useEffect(() => {
    getProperties();
  }, []);

  useEffect(() => {
    const subscription = watch((value) => {
      setValues(value as ISearchFormValues);
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch]);

  useEffect(() => {
    (async function initDictionaries() {
      await loadDictionariesForRegisteredFirearms([
        EDictionaryTypes.Make,
        EDictionaryTypes.Model,
        EDictionaryTypes.State,
        EDictionaryTypes.Caliber,
        EDictionaryTypes.Legality,
        EDictionaryTypes.Manufacturer,
        EDictionaryTypes.FirearmAction,
        EDictionaryTypes.FirearmType,
      ]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const handleResetField = (name?: string, refreshResults: boolean = true) => {
    if (name) {
      reset({ ...getValues(), [name]: '' });
    } else {
      reset(initialForm);
    }

    if (refreshResults) {
      onSubmit(getValues());
    }
  };

  const submitForm = (data: ISearchFormValues) => {
    onSubmit(data);
  };

  return (
    <SearchWrapper>
      <form onSubmit={handleSubmit(submitForm)} id="search_form">
        <Grid container columnSpacing={2.5}>
          <Grid size={{ xs: 12, md: 4, sm: 6, lg: 3 }}>
            <FormInput
              charsLimit={120}
              maxLength
              name="fireCycleId"
              label={t(ESearchFormFirearmLabels.fireCycleId)}
              control={control}
              setValue={setValue}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4, sm: 6, lg: 3 }}>
            <FormSelect
              options={mapDictionaryToOptionProp(firearmType)}
              setValue={setValue}
              name="type"
              label={t(ESearchFormFirearmLabels.type)}
              canBeEmpty
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4, sm: 6, lg: 3 }}>
            <FormAutocomplete
              name="make"
              options={mapDictionaryToOptionProp(make)}
              label={t(ESearchFormFirearmLabels.make)}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4, sm: 6, lg: 3 }}>
            <FormAutocomplete
              name="model"
              options={mapDictionaryToOptionProp(model)}
              label={t(ESearchFormFirearmLabels.model)}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4, sm: 6, lg: 3 }}>
            <FormSelect
              options={mapDictionaryToOptionProp(firearmAction)}
              name="action"
              label={t(ESearchFormFirearmLabels.action)}
              canBeEmpty
              setValue={setValue}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4, sm: 6, lg: 3 }}>
            <FormAutocomplete
              options={mapDictionaryToOptionProp(caliber)}
              name="calibre"
              label={t(ESearchFormFirearmLabels.calibre)}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4, sm: 6, lg: 3 }}>
            <FormInput
              charsLimit={120}
              maxLength
              name="serialNumber"
              label={t(ESearchFormFirearmLabels.serialNumber)}
              control={control}
              setValue={setValue}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4, sm: 6, lg: 3 }}>
            <Button
              variant={EButtonVariants.contained}
              size={EButtonSizes.small}
              id="registered_firearms-search"
              label={t('registeredFirearms.accordion.search.button')}
              type="submit"
              fullWidth
              sx={{ mt: 3.5 }}
            />
          </Grid>
        </Grid>
      </form>
      <SelectedFilters
        name="registered_firearms"
        values={values}
        noRemovable={['uniqueIds']}
        labels={ESearchFormFirearmLabels}
        handleDelete={handleResetField}
        type={ESearchFilterKeys.firearm}
        showSaveFilters={false}
        dictionaries={{
          make: prepareFilters(make, 'uniqueId', 'name'),
          model: prepareFilters(model, 'uniqueId', 'name'),
          calibre: prepareFilters(caliber, 'uniqueId', 'name'),
          action: prepareFilters(firearmAction, 'uniqueId', 'name'),
          type: prepareFilters(firearmType, 'uniqueId', 'name'),
        }}
        saveRouterStateAfterClearAll={false}
      />
    </SearchWrapper>
  );
};

export { FirearmsSearchForm };
